import React, { useEffect } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import { Megaphone, WhatsApp } from '~/ui/assets/icons';
import LogoSmall from '~/ui/assets/imgs/logoSmall.svg';
import { getCommentTypeMeta } from '~/common/commentTypes';
import { getCommentTypes } from '~/store/tickets/actions';
import { CommentType, CommentTypeName } from '~/typings/entities/Ticket';
import { Badge } from './CommentTypeBadge.styles';

type CommentTypeBadgeProps = {
  type?: CommentType | CommentTypeName | null;
  // Optional label override (e.g. "Só o cliente") while keeping the type color.
  label?: string;
  // Optional color overrides (e.g. a darker background to stand out over a tinted surface).
  bg?: string;
  text?: string;
  // Optional icon override; falls back to the type's default icon when omitted.
  icon?: React.ReactNode;
  className?: string;
};

function TypeIcon({ name }: { name?: string }) {
  switch (name) {
    case 'WHATSAPP':
      return <WhatsApp />;
    case 'PUBLIC_RA':
    case 'PRIVATE_RA':
      return <Megaphone />;
    case 'BACKOFFICE':
      // The "t" body uses currentColor; force it black (the fixed pink dot stays pink).
      return <LogoSmall style={{ color: '#000000' }} />;
    default:
      return null;
  }
}

function CommentTypeBadge({ type, label, bg, text, icon, className }: CommentTypeBadgeProps) {
  const getCommentTypesControl = useAwaitControl(getCommentTypes);
  const commentTypes: CommentType[] = getCommentTypesControl.result();

  useEffect(() => {
    if (!commentTypes?.length) getCommentTypesControl.start();
  }, [commentTypes?.length]);

  if (!type) return null;

  const meta = getCommentTypeMeta(type);
  const name = (typeof type === 'string' ? type : type?.name)?.toUpperCase();

  // The badge text always comes from the backend (mt-ticket): either the description carried by
  // the provided comment type, or the matching entry from GET /comment-types. The front never
  // hardcodes type labels, so a new message type works without any change here.
  const backendLabel =
    (typeof type !== 'string' ? type?.description : undefined) ||
    commentTypes?.find((t) => t?.name?.toUpperCase() === name)?.description;

  const resolvedLabel = label || backendLabel;

  if (!resolvedLabel) return null;

  return (
    <Badge
      palette={meta.palette}
      bg={bg || meta.colors?.bg}
      text={text || meta.colors?.text}
      className={className}
      data-testid="comment-type-badge"
    >
      {icon ?? <TypeIcon name={name} />}
      {resolvedLabel}
    </Badge>
  );
}

export default CommentTypeBadge;
